/**
 * Daten-Hooks für Auszahlungs-Nachweis & Vorschuss (Phase I, Kapitel 16) —
 * bewusst NICHT über Dexie/Outbox (wie shiftSwaps.ts): das sind Selbstauskünfte
 * mit sofortigem Bestätigungs-/Konflikt-Feedback (z. B. 409 bei doppelter
 * Auszahlungs-Bestätigung desselben Zeitraums), kein offline-editierbarer
 * eigener Datensatz.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { payoutsApi, type CreatePayoutInput } from '../lib/payouts';

const PAYOUTS_KEY = ['payouts'];
const PENDING_ADVANCES_KEY = ['payouts', 'pending-advances'];

/** Eigene Meldungen (oder, für den Chef mit `userId`, die eines bestimmten Mitarbeiters). */
export function usePayouts(userId?: string) {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: [...PAYOUTS_KEY, userId ?? 'self'],
    queryFn: () => payoutsApi.list(token!, userId),
    enabled: !!token,
  });
}

/** Nur Chef: offene Vorschuss-Meldungen der ganzen Firma. */
export function usePendingAdvances() {
  const token = useAuth((s) => s.token);
  // Gesamtprüfung 11.09.2026: Admins entscheiden nicht über Vorschüsse.
  const isAdmin = useAuth((s) => s.user?.role === 'admin' || s.user?.role === 'superadmin');
  return useQuery({
    queryKey: PENDING_ADVANCES_KEY,
    queryFn: () => payoutsApi.pendingAdvances(token!),
    enabled: !!token && !isAdmin,
    refetchInterval: 60_000,
  });
}

export function useCreatePayout() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePayoutInput) => payoutsApi.create(token!, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PAYOUTS_KEY });
      void qc.invalidateQueries({ queryKey: PENDING_ADVANCES_KEY });
    },
  });
}

export function useDecidePayout() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'confirmed' | 'rejected' }) =>
      payoutsApi.decide(token!, id, decision),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PAYOUTS_KEY });
      void qc.invalidateQueries({ queryKey: PENDING_ADVANCES_KEY });
    },
  });
}
